import { type JSX, useState } from 'react';

import type { Fakturierungszeile } from '../api/auftraege';
import { ApiFehler } from '../api/fehler';
import { ausAuftraegen } from '../api/rechnungen';
import { alsEuro, alsMenge } from '../betrag';
import { heute } from '../datum';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/** Die gewählten Zeilen je Auftrag, in der Reihenfolge der Liste. */
function nachAuftrag(
  zeilen: readonly Fakturierungszeile[],
): readonly { readonly auftrag: string; readonly nummer: string; readonly zeilen: readonly Fakturierungszeile[] }[] {
  const gruppen = new Map<string, { auftrag: string; nummer: string; zeilen: Fakturierungszeile[] }>();
  for (const z of zeilen) {
    const gruppe = gruppen.get(z.auftrag) ?? { auftrag: z.auftrag, nummer: z.auftragsnummer, zeilen: [] };
    gruppe.zeilen.push(z);
    gruppen.set(z.auftrag, gruppe);
  }
  return [...gruppen.values()];
}

/**
 * Die Sammelrechnung aus der Fakturierungsliste (RNG-06, AUF-19; Auftrag CRM-119, L8).
 *
 * „Eine Sammelrechnung kann mehrere Aufträge oder Lieferungen EINES Kunden
 * zusammenfassen." Die Auswahl trifft die Liste, das Pop-up zeigt die
 * Vorschau: welche Zeilen aus welchem Auftrag, und was zusammen offen ist.
 *
 * Es entsteht ein ENTWURF ohne Nummer (RNG-03) — verwerfbar, deshalb ohne
 * Rückfrage (Tabelle 29, ÄA-62 B). Das Belegdatum setzt die Festschreibung
 * (RNG-07); hier geht der heutige Tag mit, wie beim Weg aus der Auftragsakte.
 */
export function Sammelrechnung({
  zeilen,
  beiAngelegt,
  beiSchliessen,
}: {
  readonly zeilen: readonly Fakturierungszeile[];
  readonly beiAngelegt: (rechnung: string) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [betreff, setzeBetreff] = useState('');
  const [absetzen, setzeAbsetzen] = useState(true);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const gruppen = nachAuftrag(zeilen);
  const summe = zeilen.reduce((bisher, z) => bisher + Number(z.offenerWert), 0);
  const kunde = zeilen[0]?.kundenname ?? '';

  const anlegen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const neu = await ausAuftraegen({
        auftraege: gruppen.map((g) => g.auftrag),
        positionen: zeilen.map((z) => z.positionId),
        belegdatum: heute(),
        betreff: betreff.trim() === '' ? null : betreff.trim(),
        // RNG-09: die Anzahlungen der Aufträge als Abzug.
        anzahlungenAbsetzen: absetzen,
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  };

  const schliessen = (): void => {
    if (!laeuft) beiSchliessen();
  };

  return (
    <Dialog
      titel="Sammelrechnung anlegen"
      unterzeile={`${kunde} · ${gruppen.length} ${gruppen.length === 1 ? 'Auftrag' : 'Aufträge'}, ${zeilen.length} ${zeilen.length === 1 ? 'Position' : 'Positionen'}`}
      breite="mittel"
      beiSchliessen={schliessen}
    >
      <>
        <section aria-label="Vorschau">
          {gruppen.map((g) => (
            <div key={g.auftrag} className="feld">
              <h3 className="feld__beschriftung">Auftrag {g.nummer}</h3>
              <ul>
                {g.zeilen.map((z) => (
                  <li key={z.positionId}>
                    {z.bezeichnung} · {alsMenge(z.offeneMenge)} ·{' '}
                    <span className="zahl">{alsEuro(z.offenerWert)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p>
            Zusammen offen: <strong>{alsEuro(summe.toFixed(2))}</strong> netto.
          </p>
        </section>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="sr-betreff">
            Betreff
          </label>
          <input
            id="sr-betreff"
            type="text"
            maxLength={200}
            value={betreff}
            onChange={(e) => setzeBetreff(e.target.value)}
          />
          <span className="feld__hilfe">Leer übernimmt den Betreff des ersten Auftrags.</span>
        </div>
        <label className="liste__schalter">
          <input type="checkbox" checked={absetzen} onChange={(e) => setzeAbsetzen(e.target.checked)} />
          Anzahlungen der Aufträge absetzen
        </label>
        <p className="feld__hilfe">
          Es entsteht ein Entwurf ohne Nummer. Er lässt sich vor dem Festschreiben noch ändern oder
          löschen; die Positionen bleiben bis dahin am Auftrag offen.
        </p>
        {fehler !== undefined && <Meldung fehler={fehler} />}
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" disabled={laeuft} onClick={schliessen}>
              Abbrechen
            </button>
          }
        >
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft || zeilen.length === 0}
            aria-busy={laeuft}
            onClick={() => void anlegen()}
          >
            Entwurf anlegen
          </button>
        </Abschlussreihe>
      </>
    </Dialog>
  );
}
